import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { profileAPI } from '../services/api';
import { ScrollArea } from '../components/ui/scroll-area';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  User,
  Moon,
  Sun,
  Edit,
  Palette,
  Shield,
  Filter,
  Save,
  Loader2,
} from 'lucide-react';
import SignatureManager from '../components/email/SignatureManager';
import EmailFilterManager from '../components/email/EmailFilterManager';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await profileAPI.getProfile();
        setFirstName(res.data.first_name || '');
        setLastName(res.data.last_name || '');
        setProfileLoaded(true);
      } catch {
        // Fallback to user context
        setFirstName(user?.first_name || '');
        setLastName(user?.last_name || '');
        setProfileLoaded(true);
      }
    };
    loadProfile();
  }, [user]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await profileAPI.updateProfile({ first_name: firstName, last_name: lastName });
      toast.success('Profil mis à jour');
      if (refreshUser) refreshUser();
    } catch {
      toast.error('Erreur lors de la mise à jour du profil');
    } finally {
      setSaving(false);
    }
  };

  const displayName = [firstName, lastName].filter(Boolean).join(' ') || user?.name || 'Utilisateur';
  const initials = firstName?.[0]?.toUpperCase() || user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U';

  return (
    <div className="h-full overflow-hidden" data-testid="settings-page">
      <ScrollArea className="h-full">
        <div className="max-w-3xl mx-auto p-6 space-y-8 pb-12">
          <div>
            <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] mb-2">Paramètres</h1>
            <p className="text-muted-foreground">Gérez vos préférences personnelles</p>
          </div>

          <Tabs defaultValue="account" className="space-y-6">
            <TabsList>
              <TabsTrigger value="account" className="gap-2" data-testid="tab-account">
                <User className="w-4 h-4" />
                Compte
              </TabsTrigger>
              <TabsTrigger value="signatures" className="gap-2" data-testid="tab-signatures">
                <Edit className="w-4 h-4" />
                Signatures
              </TabsTrigger>
              <TabsTrigger value="filters" className="gap-2" data-testid="tab-filters">
                <Filter className="w-4 h-4" />
                Filtres
              </TabsTrigger>
              <TabsTrigger value="appearance" className="gap-2" data-testid="tab-appearance">
                <Palette className="w-4 h-4" />
                Apparence
              </TabsTrigger>
            </TabsList>

            {/* Account Settings */}
            <TabsContent value="account" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="font-['Plus_Jakarta_Sans']">Mon compte</CardTitle>
                  <CardDescription>Informations de votre session email</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                    <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-white text-2xl font-semibold">
                      {initials}
                    </div>
                    <div>
                      <p className="text-lg font-medium">{displayName}</p>
                      <p className="text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>

                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                      <Shield className="w-5 h-5" />
                      <span className="font-medium">Session active</span>
                    </div>
                    <p className="text-sm text-green-600 dark:text-green-500 mt-1">
                      Votre session est persistante. Vos emails restent disponibles même hors connexion.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="font-['Plus_Jakarta_Sans']">Nom d'expéditeur</CardTitle>
                  <CardDescription>
                    Ce nom apparaîtra dans le champ "De" de vos emails envoyés
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first-name">Prénom</Label>
                      <Input
                        id="first-name"
                        placeholder="Prénom"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        data-testid="profile-first-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last-name">Nom</Label>
                      <Input
                        id="last-name"
                        placeholder="Nom"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        data-testid="profile-last-name"
                      />
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Aperçu : <span className="font-medium">{displayName} &lt;{user?.email}&gt;</span>
                  </p>
                  <Button onClick={handleSaveProfile} disabled={saving} data-testid="save-profile-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Enregistrer
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Signatures with WYSIWYG */}
            <TabsContent value="signatures" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="font-['Plus_Jakarta_Sans']">Signatures email</CardTitle>
                  <CardDescription>Créez et gérez vos signatures avec l'éditeur riche</CardDescription>
                </CardHeader>
                <CardContent>
                  <SignatureManager />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Email Filters */}
            <TabsContent value="filters" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="font-['Plus_Jakarta_Sans']">Filtres automatiques</CardTitle>
                  <CardDescription>Triez automatiquement vos emails entrants avec des règles personnalisées</CardDescription>
                </CardHeader>
                <CardContent>
                  <EmailFilterManager />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Appearance */}
            <TabsContent value="appearance" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="font-['Plus_Jakarta_Sans']">Thème</CardTitle>
                  <CardDescription>Personnalisez l'apparence de votre interface</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                      <div>
                        <p className="font-medium">Mode sombre</p>
                        <p className="text-sm text-muted-foreground">
                          {theme === 'dark' ? 'Activé' : 'Désactivé'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={theme === 'dark'}
                      onCheckedChange={toggleTheme}
                      data-testid="theme-switch"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </ScrollArea>
    </div>
  );
}
